import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import BackLink from '../ui/BackLink';
import { helpCMSAPI } from './helpCMS';

/**
 * HelpArticlePage — Renders a single CMS article with anchor-tagged outline.
 */
export default function HelpArticlePage() {
  const { articleId } = useParams();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function load() {
      const a = await helpCMSAPI.getArticle(articleId);
      setArticle(a);
      setLoading(false);
    }
    load();
  }, [articleId]);

  if (loading) {
    return (
      <div className="px-6">
        <div className="animate-pulse space-y-4">
          <div className="h-6 bg-gray-200 w-1/3" style={{ borderRadius: 'var(--radius-md)' }} />
          <div className="h-4 bg-gray-100 w-2/3" style={{ borderRadius: 'var(--radius-md)' }} />
          <div className="h-4 bg-gray-100 w-1/2" style={{ borderRadius: 'var(--radius-md)' }} />
        </div>
      </div>
    );
  }

  if (!article) {
    return (
      <div className="px-6">
        <BackLink to="/help" label="Back to Help Center" className="mb-4" />
        <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>Article not found.</p>
      </div>
    );
  }

  return (
    <div className="px-6 max-w-4xl mx-auto">
      {/* Back link */}
      <BackLink to="/help" label="Back to Help Center" className="mb-4" />

      <div className="flex gap-8">
        {/* Main content */}
        <div className="flex-1 min-w-0 p-6 rounded-lg" style={{ backgroundColor: 'var(--color-surface)' }}>
          <h1 className="text-xl font-semibold mb-4" style={{ font: 'var(--text-h2)', color: 'var(--color-text-primary)' }}>
            {article.title}
          </h1>

          <div className="space-y-4">
            {article.blocks.map((block, idx) => {
              if (block.type === 'text') {
                return (
                  <p key={idx} className="text-sm leading-relaxed" style={{ color: 'var(--color-text-default)' }}>
                    {block.content}
                  </p>
                );
              }
              if (block.type === 'callout') {
                return (
                  <div
                    key={idx}
                    className="border-l-4 pl-3 py-2 text-sm"
                    style={{ borderColor: 'var(--color-action-primary)', color: 'var(--color-text-secondary)', backgroundColor: 'var(--color-surface-alt)' }}
                  >
                    {block.content}
                  </div>
                );
              }
              if (block.type === 'image') {
                return <img key={idx} src={block.content} alt="" className="max-w-full" style={{ borderRadius: 'var(--radius-md)' }} />;
              }
              if (block.type === 'video') {
                return (
                  <div key={idx} className="aspect-video bg-gray-100" style={{ borderRadius: 'var(--radius-md)' }}>
                    <iframe src={block.content} title="Video" className="w-full h-full" style={{ borderRadius: 'var(--radius-md)' }} />
                  </div>
                );
              }
              return null;
            })}
          </div>
        </div>

        {/* Anchor outline (floating right) */}
        {article.anchors && article.anchors.length > 0 && (
          <aside className="hidden lg:block w-48 flex-shrink-0">
            <nav className="sticky top-6 space-y-1">
              <p className="text-xs font-semibold uppercase mb-2" style={{ color: 'var(--color-text-muted)' }}>
                On this page
              </p>
              {article.anchors.map((anchor) => (
                <a
                  key={anchor.id}
                  href={`#${anchor.id}`}
                  className="block text-xs py-1 hover:underline"
                  style={{ color: 'var(--color-text-secondary)' }}
                >
                  {anchor.label}
                </a>
              ))}
            </nav>
          </aside>
        )}
      </div>
    </div>
  );
}
